// Exact-cent Roulette selection/response helpers. No RNG, balances or settlement ledger.
export const WHEEL = Object.freeze([0,32,15,19,4,21,2,25,17,34,6,27,13,36,11,30,8,23,10,5,24,16,33,1,20,14,31,9,22,18,29,7,28,12,35,3,26]);
export const REDS = Object.freeze([1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36]);
export const GROUPS = Object.freeze(['red','black','odd','even','low','high','dozen1','dozen2','dozen3']);
export const colorOf = number => number === 0 ? 'green' : REDS.includes(number) ? 'red' : 'black';
export const validNumber = number => Number.isInteger(number) && number >= 0 && number <= 36;
export const validId = value => typeof value === 'string' && /^[a-zA-Z0-9_-]{8,80}$/.test(value);
export const pendingKey = userId => `pb_roulette_pending:${userId}`;
export function cents(value) {
  const text = String(value).trim();
  if (!/^\d{1,8}(?:\.\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  const amount = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(amount) ? amount : null;
}
export const validChip = value => Number.isSafeInteger(value) && value >= 100 && value <= 100000;
export function betInfo(id) {
  if (typeof id !== 'string') throw new Error('Invalid target');
  if (/^number:(0|[1-9]|[12]\d|3[0-6])$/.test(id)) {
    const number = Number(id.slice(7));
    return { id, label: `Number ${number}`, short: String(number), color: colorOf(number), multiple: 36, numbers: [number] };
  }
  const labels = { red: 'Red', black: 'Black', odd: 'Odd', even: 'Even', low: '1–18', high: '19–36', dozen1: '1st 12', dozen2: '2nd 12', dozen3: '3rd 12' };
  if (!GROUPS.includes(id)) throw new Error('Invalid target');
  const numbers = Array.from({ length: 36 }, (_, i) => i + 1).filter(n =>
    id === 'red' ? REDS.includes(n) : id === 'black' ? !REDS.includes(n) : id === 'odd' ? n % 2 === 1 :
    id === 'even' ? n % 2 === 0 : id === 'low' ? n <= 18 : id === 'high' ? n >= 19 : Math.ceil(n / 12) === Number(id.slice(-1)));
  return { id, label: labels[id], short: labels[id], color: ['red','black'].includes(id) ? id : 'neutral', multiple: id.startsWith('dozen') ? 3 : 2, numbers };
}
export const stakeOf = bets => bets.reduce((sum, bet) => sum + bet.cents, 0);
export function validBets(bets, allowEmpty = false) {
  if (!Array.isArray(bets) || bets.length > 46 || (!allowEmpty && !bets.length)) return false;
  const seen = new Set();
  try { for (const bet of bets) { if (!bet || seen.has(bet.id) || !validChip(bet.cents)) return false; betInfo(bet.id); seen.add(bet.id); } }
  catch { return false; }
  return stakeOf(bets) <= 100000;
}
export function canonicalBets(bets) {
  if (!validBets(bets, true)) throw new Error('Invalid Roulette bets');
  return bets.map(({ id, cents: amount }) => ({ id, cents: amount })).sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}
export const validPending = value => !!value && validId(value.request_id) && ['coins','cash','bonus'].includes(value.currency) && validBets(value.bets);
export const sameSettings = (a, b) => a.currency === b.currency && JSON.stringify(canonicalBets(a.bets)) === JSON.stringify(canonicalBets(b.bets));
export function addChip(bets, id, amount, balanceCents) {
  if (!validBets(bets, true) || !validChip(amount)) throw new Error('Choose a chip from 1.00 to 1,000.00');
  betInfo(id);
  const next = bets.map(bet => ({ ...bet })), existing = next.find(bet => bet.id === id);
  if (existing) existing.cents += amount; else next.push({ id, cents: amount });
  if (!validBets(next)) throw new Error('The total stake limit is 1,000.00');
  if (stakeOf(next) > balanceCents) throw new Error('Not enough funds in the selected wallet');
  return next;
}
export function payoutCents(bets, number) {
  if (!validBets(bets) || !validNumber(number)) throw new Error('Invalid Roulette result');
  return bets.reduce((sum, bet) => sum + (betInfo(bet.id).numbers.includes(number) ? bet.cents * betInfo(bet.id).multiple : 0), 0);
}
export const maximumReturn = bets => bets.length ? Math.max(...WHEEL.map(n => payoutCents(bets, n))) : 0;
export const coverage = bets => new Set(bets.flatMap(bet => betInfo(bet.id).numbers)).size;
export function cleanBalances(value) {
  if (!value || ['coins','cash','bonus'].some(key => !Number.isFinite(value[key]) || value[key] < 0)) throw new Error('Invalid wallet response');
  return { coins: value.coins, cash: value.cash, bonus: value.bonus };
}
export function confirmedSpin(value) {
  if (!validPending(value) || value.status !== 'completed' || !validNumber(value.number) || value.spin !== value.number ||
      value.color !== colorOf(value.number) || typeof value.win !== 'boolean' || !Number.isFinite(value.bet) ||
      Math.abs(value.bet - stakeOf(value.bets) / 100) > 1e-8 || !Number.isFinite(value.payout) ||
      Math.abs(value.payout - payoutCents(value.bets, value.number) / 100) > 1e-8 ||
      !Number.isFinite(value.net) || Math.abs(value.net - (payoutCents(value.bets, value.number) - stakeOf(value.bets)) / 100) > 1e-8 ||
      value.win !== (value.net > 0) || value.coverage !== coverage(value.bets) || !Number.isFinite(value.edge_pct) ||
      Math.abs(value.edge_pct - 100 / 37) > 1e-8) throw new Error('Unconfirmed Roulette response');
  return { ...value, bets: canonicalBets(value.bets), ...(value.balances ? { balances: cleanBalances(value.balances) } : {}) };
}
